import { IconBox } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { nameOf } from "../../../common/Utils";
import { useGetContainerStates } from "../../../service/containers/useGetContainerStates";
import { IconPaper } from "../../paper/IconPaper";

import type { Mode } from "./ContainerIdSelection";
import { ContainerIdSelection } from "./ContainerIdSelection";
import type { SelectionData } from "./SelectionFormContext";
import { useSelectionContext } from "./SelectionFormContext";
import { useNumberInputRange } from "./useNumberInputRange";
import { useSelectionError } from "./useSelectionError";

export const SELECTION_DEBOUNCE_TIME_MS = 200;

interface SelectionProps {
  mode: Mode;
  selectedContainerNames: string[];
  setSelectedContainerNames: (selectedContainerNames: string[]) => void;
  heightOnChangeHandler?: (height: number) => void;
}

/**
 * Wrapper component that orchestrates fetching container states based on selected names,
 * calculating validation errors, and providing data to the ContainerIdSelection component
 * and the parent form context.
 *
 * It manages the interplay between manual tag input and numerical range input,
 * ensuring they are mutually exclusive and updating the shared selection state.
 *
 * @param mode - "fill" or "empty", passed down to determine validation logic and styling.
 * @param selectedContainerNames - The current list of selected container names (from parent or ContainerIdSelection). Debounced value is used for fetching.
 * @param setSelectedContainerNames - Callback to update the parent state with selected names. This is debounced internally.
 * @param heightOnChangeHandler - Optional callback for IconPaper height changes.
 */
export function Selection({
  mode,
  selectedContainerNames,
  setSelectedContainerNames,
  heightOnChangeHandler,
}: SelectionProps) {
  const selectionContext = useSelectionContext();
  const { setFieldValue } = selectionContext;

  // Holds the immediate selection (non-debounced) before updating the parent
  const [immediateSelectedIds, setImmediateSelectedIds] = useState<string[]>([]);

  const {
    numberInputFrom,
    numberInputTo,
    setNumberInputFrom,
    setNumberInputTo,
    errorInputFrom,
    errorInputTo,
    idRange, // Calculated range [from..to] as string[], or null if invalid/empty
  } = useNumberInputRange();

  const containerStatesQuery = useGetContainerStates(selectedContainerNames);

  const { data: queryData, isPending, isError, error: queryError } = containerStatesQuery;

  // Query configured with keepPreviousData to prevent UI flickering during refetches
  const containerStates = useMemo(() => queryData || [], [queryData]);

  const validationError = useSelectionError(mode, containerStates);

  /** Effect: Handle range input changes. Range takes precedence over manual input. */
  useEffect(() => {
    if (idRange) {
      // If a valid range is calculated, update the immediate selection
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: Update selection state when idRange changes from range inputs
      setImmediateSelectedIds(idRange);
    }
    // If idRange becomes null (inputs cleared), preserve the current immediateSelectedIds
    // to keep generated tags visible (as per component documentation)
  }, [idRange]);

  /** Effect: Update form context with pending status, states, and errors. */
  useEffect(() => {
    setFieldValue(nameOf<SelectionData>().isPending, isPending);
    setFieldValue(nameOf<SelectionData>().containersWithState, containerStates);

    // Determine error for the context: Query error takes precedence over validation error
    let contextError = "";
    if (isError) {
      contextError = queryError?.message || "Fehler beim Abrufen der Behälterdaten.";
    } else if (!isPending) {
      // Only show validation error if not pending and no query error occurred
      contextError = validationError;
    }
    setFieldValue(nameOf<SelectionData>().error, contextError);
  }, [containerStates, isPending, isError, queryError, validationError, setFieldValue]);

  /** Handler for manual changes within ContainerIdSelection */
  const handleManualSelectionChange = (newIds: string[]) => {
    setImmediateSelectedIds(newIds);
    // Manual input takes precedence, clear range inputs
    if (numberInputFrom !== undefined || numberInputTo !== undefined) {
      setNumberInputFrom(undefined);
      setNumberInputTo(undefined);
    }
  };

  /** Effect: Update the debounced parent state whenever the immediate selection changes */
  useEffect(() => {
    // This effect effectively debounces the setSelectedContainerNames call
    // based on changes to immediateSelectedIds (triggered by manual input or range changes).
    setSelectedContainerNames(immediateSelectedIds);
  }, [immediateSelectedIds, setSelectedContainerNames]);

  return (
    <IconPaper
      description="um Aktionen durchzuführen oder sie zu befüllen"
      icon={IconBox}
      onContentHeightChangeHandler={heightOnChangeHandler}
      p={0}
      title="Behälter auswählen"
    >
      <ContainerIdSelection
        containerStates={containerStates} // Pass the local, query-derived states for styling pills
        error={validationError} // Pass the validation error for display within the input component
        errorInputFrom={errorInputFrom}
        errorInputTo={errorInputTo}
        isPending={isPending} // Pass pending status for loading indicators
        mode={mode}
        mt={10}
        numberInputFrom={numberInputFrom}
        numberInputTo={numberInputTo}
        onManualChange={handleManualSelectionChange} // Handler for manual changes
        setNumberInputFrom={setNumberInputFrom}
        setNumberInputTo={setNumberInputTo}
        value={immediateSelectedIds} // Pass immediate state as controlled value
      />
    </IconPaper>
  );
}
